import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@monksflow/monks-ui';
import { DownloadIcon } from 'lucide-react';

import { downloadAnalysisPdf } from '@/entities/analysis';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { downloadFile, sanitizeDownloadFileName } from '@/shared/utils/downloadFile';

interface AnalysisDownloadButtonProps {
  analysisId: string;
  /** Route slug of the section to export, forwarded to the print page as `?section=`. */
  section: string;
  /** Section title, used to name the downloaded file. */
  sectionTitle: string;
  /**
   * Brand this analysis is about (`analysis.brandName`). Leads the filename so
   * two brands' exports of the same section don't collide on one name.
   */
  brand?: string;
  /** Set on the print page itself — the button suppresses itself so it never prints. */
  exporting?: boolean;
}

/**
 * Per-section "Download report" action. Never renders on the print page itself
 * (`exporting`). Downloads the current section as a server-rendered PDF.
 */
export const AnalysisDownloadButton = ({
  analysisId,
  section,
  sectionTitle,
  brand,
  exporting,
}: AnalysisDownloadButtonProps) => {
  const { t } = useTranslation('analysis');
  const workspaceId = useCurrentWorkspaceId();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!workspaceId) return;

    setIsDownloading(true);
    const notifId = notificationService.showInfo(
      t('analysisResult.analysisDownloadButton.preparingYourDownload'),
      { persist: true },
    );

    try {
      const blob = await downloadAnalysisPdf(analysisId, { section });
      // A dash rather than parentheses, purely so the name reads as a title:
      // "Acme - Brand X-Ray". Parentheses survive the sanitiser now.
      const brandPart = brand?.trim();
      const name = brandPart ? `${brandPart} - ${sectionTitle}` : sectionTitle;
      await downloadFile({
        blob,
        mimeType: 'application/pdf',
        fileName: `${sanitizeDownloadFileName(name, t('analysisResult.analysisDownloadButton.fileNameFallback'))}.pdf`,
      });
    } catch (err) {
      logger.error('Failed to download analysis PDF', err);
      notificationService.showError(
        t('analysisResult.analysisDownloadButton.failedToDownloadReport'),
      );
    } finally {
      notificationService.close(notifId);
      setIsDownloading(false);
    }
  }, [analysisId, section, sectionTitle, brand, workspaceId, t]);

  if (exporting) return null;

  return (
    <Button
      variant="outline"
      onClick={() => void handleDownload()}
      disabled={isDownloading || !workspaceId}
    >
      {isDownloading ? <Spinner className="size-4" /> : <DownloadIcon className="size-4" />}
      {t('analysisResult.analysisDownloadButton.downloadReport')}
    </Button>
  );
};
